import StepBasedBackButton from '@/src/common/components/elements/Buttons/StepBasedBackButton'
import GreetingCardDesignOptions from '@/src/common/components/elements/Forms/GreetingCardDesignOptions'
import { useBasicGiftCardContext } from '@/src/common/contexts/BasicGiftCardContext'
import {
    BACContainer,
    BACContainerLeft,
    BACContainerRight,
    ComponentContent,
} from '@/src/modules/build-a-card/StyledComponents'
import Step4LeftContent from '@/src/modules/build-a-card/content/Step4LeftContent'
import DesignGiftStep from '@/src/modules/build-a-card/physical/content/DesignGiftStep'
import SelectPackagingStep from '@/src/modules/build-a-card/physical/content/SelectPackagingStep'
import { LoadingWrapper } from '@/src/modules/pg-plus/PGPlusStyledComponents'
import { useEffect, useState } from 'react'
import { ClipLoader } from 'react-spinners'
import HeadlineEyebrow from '../../../common/components/marketing/headlines/HeadlineEyebrow'
import { CarrierOrGreetingCardPersonalizationStep } from './CarrierOrGreetingCardPersonalizationStep'
import { PhysicalOpenLoopCarrierSelectionStep } from './PhysicalOpenLoopCarrierSelectionStep'
import { GiftCustomizationForm } from './GiftCustomizationForm'
import { MobileOnly } from '@/src/common/utilities/viewportDisplay'
import ActionButtons from '@/src/modules/build-a-card/physical/content/ActionButtons'
import ShippingAndUsageContent from './content/ShippingAndUsageContent'
import { sendBasicEvent } from '@/src/common/utilities/gtag'
import { FORM_FIELDS_WRAPPER_ID } from '@/src/modules/build-a-card/constants'

function determineNumberOfSteps({
    hasGreetingCard,
}: {
    hasGreetingCard: boolean
}) {
    let totalNumberOfSteps: number = 4

    if (!hasGreetingCard) {
        totalNumberOfSteps = totalNumberOfSteps - 1
    }

    return totalNumberOfSteps
}

export const BuildACardPhysical = () => {
    const {
        designName,
        currentStep,
        setCurrentStep,
        nameOnCard,
        messageOnCard,
        hasGreetingCard,
        defaultToPredesign,
        carrierType,
        greetingCardDesignName,
    } = useBasicGiftCardContext()

    const [cloudinary, setCloudinary] = useState('')
    const [placeholderClicked, setPlaceholderClicked] = useState<boolean>(false)
    const [skipPackaging, setSkipPackaging] = useState<boolean>(false)
    const numberOfSteps: number = determineNumberOfSteps({
        hasGreetingCard: hasGreetingCard,
    })

    const showCarrierPersonalization: boolean =
        (currentStep === 3 && !hasGreetingCard) || currentStep === 4
    const showGreetingCardDesigns: boolean =
        currentStep === 3 && hasGreetingCard

    function getCloudinaryUrl(image: string) {
        if (image) {
            // predesigns can be located in the GCG or PGSite bucket
            // Get everything from the bucket to the end of the url
            const cloudinaryImagePath = image.slice(
                image.search('GCG/|PGSite/')
            )
            // Add card image path to transformation url with overlay
            setCloudinary(
                `https://res.cloudinary.com/gift-card-granny/image/upload/f_auto,fl_relative,h_1.0,l_GCG:card_overlays:Visa_GCG_Number_z4ezjh,w_1.0/co_white,fl_relative,g_south_west,l_text:Arial_14_stroke:${nameOnCard}%250A${messageOnCard},x_0.1,y_0.1/co_black,e_outline:outer:1:0/c_scale,f_auto,h_208,w_325/${cloudinaryImagePath}`
            )
        } else {
            console.log('image url required to build cloudinary url')
        }
    }

    useEffect(() => {
        if (currentStep === 2) {
            sendBasicEvent('select_carrier')
        }
        if (showGreetingCardDesigns && !greetingCardDesignName) {
            sendBasicEvent('select_greeting_card')
        }
        if (showCarrierPersonalization) {
            sendBasicEvent('customize_carrier', {
                type: carrierType,
                designName: hasGreetingCard ? greetingCardDesignName : '',
            })
        }
    }, [
        carrierType,
        currentStep,
        greetingCardDesignName,
        hasGreetingCard,
        showCarrierPersonalization,
        showGreetingCardDesigns,
    ])

    if (!designName) {
        return (
            <LoadingWrapper>
                <ClipLoader size={50} />
            </LoadingWrapper>
        )
    }

    // Step 1 - Basic card details (design, denom, etc.)
    // Step 2 - Packaging selection (greeting card vs carrier)
    // Step 3 (optional) - Greeting card selection
    // Step 3 or 4 - Carrier or greeting card personalization
    return (
        <ComponentContent>
            <BACContainer id={FORM_FIELDS_WRAPPER_ID}>
                <BACContainerLeft>
                    <StepBasedBackButton
                        currentStep={currentStep}
                        setCurrentStep={setCurrentStep}
                        skipPackaging={skipPackaging}
                        hideBackButton={currentStep < 2}
                    />
                    <HeadlineEyebrow
                        label={`Step ${currentStep} of ${numberOfSteps}`}
                    />
                    {currentStep === 1 && (
                        <DesignGiftStep
                            setPlaceholderClicked={setPlaceholderClicked}
                        />
                    )}
                    {currentStep === 2 && <SelectPackagingStep />}
                    {(currentStep === 3 || currentStep === 4) && (
                        <Step4LeftContent cloudinary={cloudinary} />
                    )}
                </BACContainerLeft>
                <BACContainerRight>
                    {currentStep === 1 && (
                        <GiftCustomizationForm
                            setCloudinaryUrl={getCloudinaryUrl}
                            setPlaceholderClicked={setPlaceholderClicked}
                            placeholderIsClicked={placeholderClicked}
                            setSkipPackaging={setSkipPackaging}
                        />
                    )}
                    {currentStep === 2 && (
                        <PhysicalOpenLoopCarrierSelectionStep />
                    )}
                    {showGreetingCardDesigns && <GreetingCardDesignOptions />}
                    {showCarrierPersonalization && (
                        <CarrierOrGreetingCardPersonalizationStep />
                    )}
                </BACContainerRight>
                {currentStep === 1 && (
                    <MobileOnly>
                        <BACContainerLeft>
                            <ActionButtons
                                defaultToPredesign={defaultToPredesign}
                            />
                            <ShippingAndUsageContent
                                showPredesignMessage={defaultToPredesign}
                            />
                        </BACContainerLeft>
                    </MobileOnly>
                )}
            </BACContainer>
        </ComponentContent>
    )
}
